'use client';

import { useEffect, useRef, useState } from 'react';
import { PenLine, Trash2, Upload, Loader2, ImageOff } from 'lucide-react';
import { avisoError, avisoExito } from '@/lib/avisoCentral';
import { descargarBlob } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

/**
 * Gestor de firma reutilizable: muestra la vista previa de la firma actual y permite
 * subir/reemplazar o eliminar. Se usa tanto en "Mi firma" (perfil propio) como en la
 * gestión de un usuario por un administrador; las llamadas concretas se inyectan.
 */
export default function GestorFirma({
  firmaKeyInicial,
  onSubir,
  onEliminar,
  onCambio,
}: {
  firmaKeyInicial: string | null;
  onSubir: (file: File) => Promise<string | null>; // devuelve la nueva firma_key
  onEliminar: () => Promise<void>;
  onCambio?: () => void;
}) {
  const [key, setKey] = useState<string | null>(firmaKeyInicial);
  const [url, setUrl] = useState<string | null>(null);
  const [cargandoPrev, setCargandoPrev] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const urlRef = useRef<string | null>(null);

  // Carga la vista previa (blob autenticado) cuando cambia la clave.
  useEffect(() => {
    let cancel = false;
    if (urlRef.current) { URL.revokeObjectURL(urlRef.current); urlRef.current = null; }
    setUrl(null);
    if (!key) return;
    setCargandoPrev(true);
    descargarBlob(`/documentos/descargar?path=${encodeURIComponent(key)}`)
      .then((b) => {
        if (cancel) return;
        const u = URL.createObjectURL(b);
        urlRef.current = u;
        setUrl(u);
      })
      .catch(() => { /* sin preview */ })
      .finally(() => { if (!cancel) setCargandoPrev(false); });
    return () => { cancel = true; };
  }, [key]);

  // Libera el object URL al desmontar.
  useEffect(() => () => { if (urlRef.current) URL.revokeObjectURL(urlRef.current); }, []);

  async function subir() {
    if (!file) { avisoError('Seleccione una imagen de firma.'); return; }
    setOcupado(true);
    try {
      const nueva = await onSubir(file);
      setFile(null);
      setKey(nueva ?? null);
      avisoExito('Firma guardada.');
      onCambio?.();
    } catch (e) {
      avisoError('No se pudo guardar la firma', e instanceof Error ? e.message : undefined);
    } finally {
      setOcupado(false);
    }
  }

  async function eliminar() {
    setOcupado(true);
    try {
      await onEliminar();
      setKey(null);
      avisoExito('Firma eliminada.');
      onCambio?.();
    } catch (e) {
      avisoError('No se pudo eliminar la firma', e instanceof Error ? e.message : undefined);
    } finally {
      setOcupado(false);
    }
  }

  return (
    <div className="space-y-3">
      {/* Vista previa */}
      <div className="flex min-h-[96px] items-center justify-center rounded-md border bg-muted/30 p-3">
        {cargandoPrev ? (
          <Loader2 className="size-5 animate-spin text-muted-foreground" />
        ) : url ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={url} alt="Firma" className="max-h-24 max-w-full object-contain" />
        ) : (
          <span className="flex items-center gap-2 text-sm text-muted-foreground">
            <ImageOff className="size-4" /> Sin firma cargada
          </span>
        )}
      </div>

      {/* Subir / reemplazar */}
      <div className="space-y-2">
        <Input
          type="file"
          accept="image/png,image/jpeg"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          disabled={ocupado}
        />
        <p className="text-xs text-muted-foreground">
          PNG o JPG. Se recomienda PNG con fondo transparente.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button type="button" onClick={subir} disabled={ocupado || !file}>
            {ocupado ? <Loader2 className="size-4 animate-spin" /> : <Upload className="size-4" />}
            {key ? 'Reemplazar firma' : 'Subir firma'}
          </Button>
          {key && (
            <Button type="button" variant="outline" onClick={eliminar} disabled={ocupado}>
              <Trash2 className="size-4" /> Eliminar
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// Icono exportado por conveniencia (para encabezados de la sección de firma).
export { PenLine as IconoFirma };
